import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, AlertCircle, AlertTriangle, Info, Filter, Eye, X } from 'lucide-react';
import { api } from '@/lib/api';
import InsightDetailModal from './InsightDetailModal';
import { colors, getNeumorphicShadow, getNeumorphicHover } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const BusinessInsightsSection = ({ onModalStateChange }) => {
  const { theme } = useTheme();
  const currentColors = colors[theme];

  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, critical: 0, warning: 0 });
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [topics, setTopics] = useState([]);
  const [filters, setFilters] = useState({ severity: '', topic: '', status: '', org_id: '', outlet_id: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);

  const loadData = useCallback(async (filterValues) => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filterValues || {}).filter(([, v]) => v !== ''));
      const [insRes, sumRes, orgsRes, outRes, typRes] = await Promise.all([
        api.getInsights(clean),
        api.getInsightsSummary(),
        api.getOrganizations(),
        api.getOutlets(),
        api.getInsightTypes(),
      ]);
      setInsights(insRes.data);
      setSummary(sumRes.data);
      setOrganizations(orgsRes.data);
      setOutlets(outRes.data);
      setTopics(typRes.data?.topics || []);
    } catch (e) {
      console.error('Error loading insights:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData({});
  }, [loadData]);

  const applyFilters = () => {
    loadData(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const empty = { severity: '', topic: '', status: '', org_id: '', outlet_id: '' };
    setFilters(empty);
    loadData(empty);
  };

  const handleStatusUpdate = async (insightId, status) => {
    try {
      await api.updateInsightStatus(insightId, status);
      setSelectedInsight(null);
      onModalStateChange?.(false);
      loadData(filters);
    } catch (e) {
      console.error(e);
    }
  };

  const getSeverityIcon = (sev) => {
    if (sev === 'CRITICAL') return <AlertCircle size={18} />;
    if (sev === 'WARNING') return <AlertTriangle size={18} />;
    return <Info size={18} />;
  };

  const getSeverityColor = (sev) => {
    if (sev === 'CRITICAL') return currentColors.accentRed;
    if (sev === 'WARNING') return currentColors.accentOrange;
    return currentColors.accentBlue;
  };

  const getStatusColor = (st) => {
    if (st === 'NEW') return currentColors.accentBlue;
    if (st === 'ACKED') return currentColors.accentYellow;
    if (st === 'RESOLVED') return currentColors.accentGreen;
    return currentColors.textSecondary;
  };

  const summaryCards = [
    { label: 'Total', value: summary.total, icon: Lightbulb, color: currentColors.accentBlue },
    { label: 'New', value: summary.new, icon: Info, color: currentColors.accentGreen },
    { label: 'Critical', value: summary.critical, icon: AlertCircle, color: currentColors.accentRed },
    { label: 'Warnings', value: summary.warning, icon: AlertTriangle, color: currentColors.accentOrange },
  ];

  const filterFields = [
    { key: 'severity', label: 'Severity', options: [{ v: 'CRITICAL', l: 'Critical' }, { v: 'WARNING', l: 'Warning' }, { v: 'INFO', l: 'Info' }] },
    { key: 'status', label: 'Status', options: [{ v: 'NEW', l: 'New' }, { v: 'ACKED', l: 'Acknowledged' }, { v: 'RESOLVED', l: 'Resolved' }, { v: 'DISMISSED', l: 'Dismissed' }] },
    { key: 'topic', label: 'Topic', options: topics.map((t) => ({ v: t, l: t.replace(/_/g, ' ') })) },
    { key: 'org_id', label: 'Organization', options: organizations.map((o) => ({ v: o.org_id, l: o.org_name })) },
    { key: 'outlet_id', label: 'Outlet', options: outlets.map((o) => ({ v: o.outlet_id, l: o.outlet_name })) },
  ];

  return (
    <motion.div className="mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="business-insights-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Lightbulb size={20} style={{ color: currentColors.accentBlue }} />
          <h2 className="text-lg font-bold" style={{ color: currentColors.textPrimary }}>
            Business Insights
          </h2>
          {summary.new > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{ background: currentColors.accentBlue + '20', color: currentColors.accentBlue }}>
              {summary.new} new
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-1.5"
            style={{ background: currentColors.cardBg, color: currentColors.accentBlue, boxShadow: getNeumorphicShadow(theme) }}
            data-testid="insights-toggle-filters-btn"
          >
            <Filter size={14} /> {showFilters ? 'Hide' : 'Filters'}
          </button>
          {Object.values(filters).some((v) => v) && (
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 text-sm rounded-lg"
              style={{ background: currentColors.cardBg, color: currentColors.textSecondary, boxShadow: getNeumorphicShadow(theme) }}
              data-testid="insights-clear-filters-btn"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {summaryCards.map((card) => (
          <motion.div
            key={card.label}
            className="rounded-xl p-3 cursor-pointer"
            style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
            whileHover={{ scale: 1.02 }}
            onClick={() => {
              if (card.label === 'Critical') {
                setFilters((f) => ({ ...f, severity: 'CRITICAL' }));
                loadData({ ...filters, severity: 'CRITICAL' });
              } else if (card.label === 'Warnings') {
                setFilters((f) => ({ ...f, severity: 'WARNING' }));
                loadData({ ...filters, severity: 'WARNING' });
              } else if (card.label === 'New') {
                setFilters((f) => ({ ...f, status: 'NEW' }));
                loadData({ ...filters, status: 'NEW' });
              } else {
                clearFilters();
              }
            }}
            data-testid={`insight-stat-${card.label.toLowerCase()}`}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: card.color + '20' }}>
                <card.icon size={16} style={{ color: card.color }} />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: currentColors.textPrimary }}>{card.value}</p>
                <p className="text-xs" style={{ color: currentColors.textSecondary }}>{card.label}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filter Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl p-4 mb-4 overflow-hidden"
            style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
            data-testid="insights-filters-panel"
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {filterFields.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-medium mb-1" style={{ color: currentColors.textPrimary }}>
                    {f.label}
                  </label>
                  <select
                    value={filters[f.key]}
                    onChange={(e) => setFilters((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm rounded-lg"
                    style={{ background: currentColors.background, color: currentColors.textPrimary, border: `1px solid ${currentColors.border}` }}
                  >
                    <option value="">All</option>
                    {f.options.map((o) => (
                      <option key={o.v} value={o.v}>
                        {o.l}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <button
              onClick={applyFilters}
              className="mt-3 px-4 py-2 text-sm font-medium text-white rounded-lg"
              style={{ background: currentColors.accentBlue }}
              data-testid="insights-apply-filters-btn"
            >
              Apply Filters
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Insight List */}
      {loading ? (
        <div className="flex items-center justify-center h-32" data-testid="insights-loading">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: currentColors.accentBlue }} />
        </div>
      ) : insights.length === 0 ? (
        <div
          className="rounded-xl p-8 text-center"
          style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
          data-testid="insights-empty"
        >
          <Lightbulb size={36} className="mx-auto mb-3" style={{ color: currentColors.textSecondary }} />
          <p className="font-medium" style={{ color: currentColors.textPrimary }}>No insights found</p>
          <p className="text-xs mt-1" style={{ color: currentColors.textSecondary }}>No insights match current filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {insights.map((insight, i) => (
            <motion.div
              key={insight.insight_id}
              className="rounded-xl p-4 cursor-pointer border-l-4"
              style={{
                background: currentColors.cardBg,
                boxShadow: getNeumorphicShadow(theme),
                borderLeftColor: getSeverityColor(insight.severity),
              }}
              onClick={() => { setSelectedInsight(insight); onModalStateChange?.(true); }}
              whileHover={{ scale: 1.01, boxShadow: getNeumorphicHover(theme) }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              data-testid={`insight-card-${insight.insight_id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ color: getSeverityColor(insight.severity) }}
                >
                  {getSeverityIcon(insight.severity)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-sm" style={{ color: currentColors.textPrimary }}>
                      {insight.title}
                    </h3>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                      style={{ background: getStatusColor(insight.status) + '20', color: getStatusColor(insight.status) }}
                    >
                      {insight.status}
                    </span>
                  </div>
                  <p className="text-xs mb-2" style={{ color: currentColors.textSecondary }}>
                    {insight.description}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs" style={{ color: currentColors.textSecondary }}>
                    <span className="px-2 py-0.5 rounded-lg" style={{ background: currentColors.background }}>
                      {insight.topic?.replace(/_/g, ' ')}
                    </span>
                    <span>{new Date(insight.generated_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <Eye size={16} style={{ color: currentColors.accentBlue }} className="flex-shrink-0 mt-1" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Insight Detail Modal */}
      {selectedInsight && (
        <InsightDetailModal
          insight={selectedInsight}
          onClose={() => { setSelectedInsight(null); onModalStateChange?.(false); }}
          onStatusUpdate={handleStatusUpdate}
          currentColors={currentColors}
        />
      )}
    </motion.div>
  );
};

export default BusinessInsightsSection;
